<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import { useProductsStore } from 'dashboard/stores/products';
import {
  cellText,
  STANDARD_FIELD_TYPES,
} from 'dashboard/components-next/Products/productFormat';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ProductFormDialog from 'dashboard/components-next/Products/ProductFormDialog.vue';
import ProductFieldsDialog from 'dashboard/components-next/Products/ProductFieldsDialog.vue';

const { t } = useI18n();
const store = useStore();
const productsStore = useProductsStore();
const { orderedFields } = useFieldOrder('product');

const formDialogRef = ref(null);
const fieldsDialogRef = ref(null);
const deleteDialogRef = ref(null);
const productToDelete = ref(null);

const searchQuery = ref('');
const currentPage = ref(1);
const groupFilter = ref('');

const products = computed(() => productsStore.records);
const totalCount = computed(() => productsStore.meta.totalCount || 0);
const isFetching = computed(() => productsStore.uiFlags.fetching);
const hasFilters = computed(
  () => !!searchQuery.value.trim() || !!groupFilter.value
);

const cellLabels = computed(() => ({
  yes: t('FILTER.ATTRIBUTE_LABELS.TRUE'),
  no: t('FILTER.ATTRIBUTE_LABELS.FALSE'),
}));

const groupOptions = computed(() => [
  { value: '', label: t('PRODUCTS.ALL_GROUPS') },
  ...productsStore.groups.map(group => ({
    value: group.id,
    label: group.name,
  })),
]);

const headerLabel = field =>
  field.type === 'custom'
    ? field.attribute.attributeDisplayName
    : t(field.labelKey);

const isImageField = field =>
  STANDARD_FIELD_TYPES[field.key] === 'image' && field.type === 'standard';
const textOf = (product, field) => cellText(product, field, cellLabels.value);

const load = async () => {
  try {
    await productsStore.fetch({
      page: currentPage.value,
      q: searchQuery.value.trim(),
      productGroupId: groupFilter.value,
    });
  } catch {
    useAlert(t('PRODUCTS.MESSAGES.LOAD_ERROR'));
  }
};

const debouncedSearch = debounce(() => {
  currentPage.value = 1;
  load();
}, 300);

const onSearch = value => {
  searchQuery.value = value;
  debouncedSearch();
};

const onGroupChange = value => {
  groupFilter.value = value;
  currentPage.value = 1;
  load();
};

const onPageChange = page => {
  currentPage.value = page;
  load();
};

const askDelete = product => {
  productToDelete.value = product;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await productsStore.remove(productToDelete.value.id);
    useAlert(t('PRODUCTS.MESSAGES.DELETE_SUCCESS'));
    // Página ficou vazia depois de excluir o último item dela: volta uma.
    if (!products.value.length && currentPage.value > 1) currentPage.value -= 1;
    load();
  } catch {
    useAlert(t('PRODUCTS.MESSAGES.DELETE_ERROR'));
  } finally {
    deleteDialogRef.value?.close();
    productToDelete.value = null;
  }
};

onMounted(() => {
  store.dispatch('attributes/get');
  productsStore.fetchGroups();
  load();
});
</script>

<template>
  <section class="flex w-full h-full overflow-hidden bg-n-surface-1">
    <div class="flex flex-col w-full h-full">
      <header class="sticky top-0 z-20 px-6">
        <div
          class="flex flex-wrap items-center justify-between w-full gap-3 py-6 mx-auto max-w-6xl"
        >
          <div class="flex items-baseline gap-3 min-w-0">
            <span class="text-xl font-medium truncate text-n-slate-12">
              {{ t('PRODUCTS.TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11 shrink-0">
              {{ t('PRODUCTS.COUNT', { count: totalCount }, totalCount) }}
            </span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <div class="w-48">
              <ComboBox
                :model-value="groupFilter"
                :options="groupOptions"
                :placeholder="t('PRODUCTS.ALL_GROUPS')"
                @update:model-value="onGroupChange"
              />
            </div>
            <Input
              :model-value="searchQuery"
              type="search"
              :placeholder="t('PRODUCTS.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-56"
              @input="onSearch($event.target.value)"
            >
              <template #prefix>
                <Icon
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                  icon="i-lucide-search"
                />
              </template>
            </Input>
            <Button
              type="button"
              sm
              faded
              slate
              icon="i-lucide-settings-2"
              :label="t('PRODUCTS.FIELDS_BUTTON')"
              @click="fieldsDialogRef?.open()"
            />
            <Button
              type="button"
              sm
              solid
              blue
              icon="i-lucide-plus"
              :label="t('PRODUCTS.ADD')"
              @click="formDialogRef?.open()"
            />
          </div>
        </div>
      </header>

      <main class="flex-1 px-6 overflow-y-auto">
        <div class="w-full pb-6 mx-auto max-w-6xl">
          <p
            v-if="isFetching && !products.length"
            class="py-20 text-center text-n-slate-11"
          >
            {{ t('PRODUCTS.LOADING') }}
          </p>
          <div
            v-else-if="!products.length"
            class="flex flex-col items-center gap-2 py-20 text-center"
          >
            <span class="text-base font-medium text-n-slate-12">
              {{
                hasFilters
                  ? t('PRODUCTS.EMPTY.NO_RESULTS')
                  : t('PRODUCTS.EMPTY.TITLE')
              }}
            </span>
            <span v-if="!hasFilters" class="text-sm text-n-slate-11">
              {{ t('PRODUCTS.EMPTY.DESCRIPTION') }}
            </span>
          </div>
          <div
            v-else
            class="overflow-x-auto border thin-scrollbar rounded-xl border-n-weak bg-n-solid-1"
          >
            <table class="w-full text-sm text-start">
              <thead>
                <tr class="border-b border-n-weak text-n-slate-11">
                  <th
                    class="px-4 py-3 font-medium text-start whitespace-nowrap"
                  >
                    {{ t('PRODUCTS.FIELDS.NAME') }}
                  </th>
                  <th
                    v-for="field in orderedFields"
                    :key="field.id"
                    class="px-4 py-3 font-medium text-start whitespace-nowrap"
                  >
                    {{ headerLabel(field) }}
                  </th>
                  <th class="w-24 px-4 py-3" />
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="product in products"
                  :key="product.id"
                  class="cursor-pointer hover:bg-n-alpha-1"
                  @click="formDialogRef?.open(product)"
                >
                  <td
                    class="px-4 py-3 font-medium whitespace-nowrap text-n-slate-12"
                  >
                    {{ product.name }}
                  </td>
                  <td
                    v-for="field in orderedFields"
                    :key="field.id"
                    class="px-4 py-3 whitespace-nowrap text-n-slate-12"
                  >
                    <img
                      v-if="isImageField(field) && product.imageUrl"
                      :src="product.imageUrl"
                      class="object-cover rounded-md size-10"
                      alt=""
                    />
                    <span
                      v-else-if="!isImageField(field)"
                      class="block truncate max-w-64"
                      :title="textOf(product, field)"
                    >
                      {{ textOf(product, field) }}
                    </span>
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex justify-end gap-1">
                      <Button
                        type="button"
                        sm
                        ghost
                        slate
                        icon="i-lucide-pencil"
                        :title="t('PRODUCTS.TABLE.EDIT')"
                        @click.stop="formDialogRef?.open(product)"
                      />
                      <Button
                        type="button"
                        sm
                        ghost
                        ruby
                        icon="i-lucide-trash"
                        :title="t('PRODUCTS.TABLE.DELETE')"
                        @click.stop="askDelete(product)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <footer
        v-if="totalCount > productsStore.meta.perPage"
        class="sticky bottom-0 z-0"
      >
        <PaginationFooter
          current-page-info="COMPANIES_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="productsStore.meta.perPage"
          class="max-w-[72rem]"
          @update:current-page="onPageChange"
        />
      </footer>
    </div>

    <ProductFormDialog ref="formDialogRef" @saved="load" />
    <ProductFieldsDialog ref="fieldsDialogRef" />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('PRODUCTS.DELETE.TITLE')"
      :description="
        t('PRODUCTS.DELETE.DESCRIPTION', { name: productToDelete?.name || '' })
      "
      :confirm-button-label="t('PRODUCTS.DELETE.CONFIRM')"
      @confirm="confirmDelete"
    />
  </section>
</template>
